import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { HeaderEventEmitter } from "../../../helper-methods/HeaderEvents";
import {
  deepClone,
  errorHandler,
  formatCurrencyValue,
  formatOnlyDateMoment,
  getDropdownColor,
  getLoggedInUserId,
  isRegularUser,
  openUrlOnNewTab,
  refreshFunc,
  showToast,
  structureQueryParams,
} from "../../../helper-methods";
import { BASE_URL, agentClosingStatus } from "../../../config";
import {
  agentFetchAllStatusReport,
  signingCompanyFetchAllStatusReport,
} from "../../../http/http-calls";
import {
  Button,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Card,
} from "reactstrap";

import { useSelector } from "react-redux";
import CustomPagination from "../components/CustomPagination";
import useScreenWidth from "../components/HelpModule/useScreenWidth";
import { useHistory } from "react-router-dom";
import SkeletonLoading from "../components/Skeletons/SkeletonLoading";
import SvgIcons from "../components/SvgIcons";
const DEFAULT_KEY_FOR_COMPANY = "signingcompany";

const CustomCard = lazy(() => import("../components/CustomCard"));
const AgentCompantStatusReportFilterComponent = lazy(() =>
  import("../components/AgentCompantStatusReportFilterComponent")
);
const TableFormatDataForStatusReport = lazy(() =>
  import("../components/TableFormatDataForStatusReport")
);
const CardDataForStatusReport = lazy(() =>
  import("../components/CardDataForStatusReport")
);

const AgentCompanyStatusReport = () => {
  const userData = useSelector((state) => state.userData);
  const { isForMobile } = useScreenWidth();
  const history = useHistory();
  const hasMounted = useRef(false);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Pending");
  const [pageNumber, setPageNumber] = useState(1);
  const [isClassAdded, setIsClassAdded] = useState(false);
  const [payload, setPayload] = useState({
    filters: {
      states: "",
    },
  });
  const payloadRef = useRef(payload);

  const [closings, setClosings] = useState([]);
  const [closingsBackup, setClosingsBackup] = useState([]);

  const filterShowMobile = () => {
    setIsClassAdded((prevState) => !prevState);
  };

  const _persistFilter = () => {
    const currentPayload = payloadRef.current;
    if (
      (currentPayload?.filters && currentPayload?.filters.states) ||
      currentPayload?.pageNumber !== 0
    ) {
      let data = { ...currentPayload, pageNumber: pageNumber };
      if (userData?.type === DEFAULT_KEY_FOR_COMPANY) {
        localStorage.companystatusreport = JSON.stringify(data);
      } else {
        localStorage.agentstatusreport = JSON.stringify(data);
      }
    } else {
      if (userData?.type === DEFAULT_KEY_FOR_COMPANY) {
        delete localStorage.companystatusreport;
      } else {
        delete localStorage.agentstatusreport;
      }
    }
  };

  const _filterOnChange = (type, value) => {
    setLoading(true);

    let newFilters = deepClone(payload);
    newFilters.filters[type] = value;

    setPayload(newFilters);
  };

  const _paginateArray = (
    currentPage,
    itemsPerPage = 10,
    newclosingsBackup,
    tab
  ) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    if (newclosingsBackup && tab === undefined) {
      let newArray = newclosingsBackup
        .filter((item) => item.status === activeTab)
        .slice(startIndex, endIndex);
      setClosings(newArray);
      setPageNumber(currentPage);
    } else if (tab) {
      let newArray = newclosingsBackup
        .filter((item) => item.status === tab)
        .slice(startIndex, endIndex);
      setClosings(newArray);
      setPageNumber(currentPage);
    } else {
      let newArray = closingsBackup
        .filter((item) => item.status === activeTab)
        .slice(startIndex, endIndex);
      setClosings(newArray);
      setPageNumber(currentPage);
    }
  };

  //For handle tab changes
  const _toggleTab = async (tab) => {
    if (tab === activeTab) {
      return;
    }
    if (userData?.type === DEFAULT_KEY_FOR_COMPANY) {
      try {
        setActiveTab(tab);
        setPageNumber(1);

        _paginateArray(1, 10, closingsBackup, tab);
      } catch (error) {
        errorHandler(error);
        setLoading(false);
        setActiveTab("Pending");
        setPageNumber(1);
      }
    } else {
      try {
        setActiveTab(tab);
        setPageNumber(1);
      } catch (error) {
        errorHandler(error);
        setLoading(false);
        setActiveTab("Pending");
        setPageNumber(1);
      }
    }
  };

  const _getAllStatusReport = async () => {
    setLoading(true);
    const newPayload = deepClone(payload);

    if (newPayload.filters?.dateRangeValue) {
      newPayload.filters.startDate = formatOnlyDateMoment(
        newPayload.filters?.dateRangeValue[0]
      );
      newPayload.filters.endDate = formatOnlyDateMoment(
        newPayload.filters?.dateRangeValue[1]
      );
      delete newPayload.filters.dateRangeValue;
    }

    if (userData?.type === DEFAULT_KEY_FOR_COMPANY) {
      try {
        let res = await signingCompanyFetchAllStatusReport(newPayload);
        setClosings(res?.closings?.slice(0, 10) || []);
        setClosingsBackup(res?.closings);
        setLoading(false);
        if (res?.closings.length) {
          _paginateArray(pageNumber, 10, res?.closings);
        }
      } catch (error) {
        setLoading(false);
        errorHandler(error);
      }
    } else {
      try {
        const res = await agentFetchAllStatusReport(newPayload);
        setClosings(res?.closings);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching status report:", error);
        setLoading(false);
      }
    }
  };

  //For handle local storage company and agent
  const _setFilterDataFromLocalStorage = () => {
    if (userData?.type === DEFAULT_KEY_FOR_COMPANY) {
      if (localStorage && localStorage.companystatusreport) {
        try {
          const localStorageData = JSON.parse(localStorage.companystatusreport);
          setPageNumber(localStorageData?.pageNumber);
          delete localStorageData?.pageNumber;
          setPayload(localStorageData);
          // _getAllStatusReport();
        } catch (error) {
          console.error("Error while parsing local storage data:", error);
          _getAllStatusReport();
        }
      } else {
        _getAllStatusReport();
      }
    } else {
      if (localStorage && localStorage.agentstatusreport) {
        try {
          const localStorageData = JSON.parse(localStorage.agentstatusreport);
          setPageNumber(localStorageData?.pageNumber);
          delete localStorageData?.pageNumber;
          setPayload(localStorageData);
          // _getAllStatusReport();
        } catch (error) {
          console.error("Error parsing saved filters:", error);
          _getAllStatusReport();
        }
      } else {
        console.log("No saved filters found.");
        _getAllStatusReport();
      }
    }
  };

  //Reset filter for company and agent
  const _resetFilter = () => {
    setPayload({
      filters: {
        states: "",
      },
    });
    if (userData?.type === DEFAULT_KEY_FOR_COMPANY) {
      refreshFunc("company-reporting-status-report-responsive");
    } else {
      refreshFunc("agent-reporting-status-report-responsive");
    }
  };

  // This function use for download data company and agent
  const _downloadData = (action = "download") => {
    if (!Object.keys(closings).length) {
      showToast(`No data for ${action}`, "error");
      return;
    }

    const finalPayload = {
      userId: getLoggedInUserId(),
    };

    if (payload?.filters.states && payload?.filters.states.length) {
      finalPayload.filters["states"] = payload?.filters.states;
    }

    if (payload?.filters?.dateRangeValue) {
      finalPayload["startDate"] = formatOnlyDateMoment(
        payload?.filters?.dateRangeValue[0]
      );
      finalPayload["endDate"] = formatOnlyDateMoment(
        payload?.filters?.dateRangeValue[1]
      );
    }

    const queryParams = structureQueryParams(finalPayload);

    if (action === "download")
      openUrlOnNewTab(
        `${BASE_URL}/${userData?.type}/download/status-report${queryParams}`
      );
  };

  //agent fee calculate for company and agent
  const _calculateAgentTotalFee = (array) => {
    if (userData?.type === DEFAULT_KEY_FOR_COMPANY) {
      return array?.reduce((totalFee, currentItem) => {
        if (
          currentItem.status === activeTab &&
          currentItem.agentFee !== undefined
        ) {
          return totalFee + currentItem.agentFee;
        } else {
          return totalFee;
        }
      }, 0);
    } else {
      let arr = array.map((item) =>
        item.agentFee !== undefined ? item.agentFee : 0
      );

      return arr.reduce((a, b) => a + b, 0);
    }
  };

  //For manage data mobile view
  const _cardDataFormat = (row) => {
    return (
      <Suspense fallback={<></>}>
        <CardDataForStatusReport userData={userData} row={row} />
      </Suspense>
    );
  };

  //For manage Data Table view
  const _tableFormatWithArray = (data) => {
    return (
      <Suspense fallback={<></>}>
        <TableFormatDataForStatusReport
          userData={userData}
          data={data}
          loading={loading}
        />
      </Suspense>
    );
  };

  const _getStatusIcon = (activeTab) => {
    const currentStatusConfig = agentClosingStatus?.find(
      (each) => each.value === activeTab
    );
    console.log("currentStatusConfig >>", currentStatusConfig);
    return currentStatusConfig?.icon;
  };

  useEffect(() => {
    setLoading(true);
    _setFilterDataFromLocalStorage();
    if (userData?.type === DEFAULT_KEY_FOR_COMPANY) {
      const resetFilterSubscription = HeaderEventEmitter?.subscribe(
        "reset-filter",
        () => {
          console.log("filter reset hit");
          _resetFilter();
          refreshFunc("company-reporting-status-report");
        }
      );

      const downloadStatusReportSubscription = HeaderEventEmitter?.subscribe(
        "download-status-report",
        () => {
          console.log("status-report");
          _downloadData();
        }
      );

      return () => {
        resetFilterSubscription?.unsubscribe();
        downloadStatusReportSubscription?.unsubscribe();
        _persistFilter();
      };
    } else {
      if (isRegularUser()) {
        HeaderEventEmitter?.subscribe("reset-filter", () => {
          console.log("filter reset hit");
          _resetFilter();
          refreshFunc("agent-reporting-status-report");
        });
        return () => {
          _persistFilter();
        };
      } else {
        history.goBack();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    payloadRef.current = { ...payload, pageNumber: pageNumber };
    if (hasMounted.current) {
      _getAllStatusReport();
    } else {
      hasMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useEffect(() => {
    if (payloadRef.current) {
      payloadRef.current = {
        ...payloadRef.current,
        pageNumber: pageNumber,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  return (
    <div className="content">
      <div className="responsiveTitle">
        <h2>Status Report</h2>
        <div className="rightSide">
          <Button
            id="company-reporting-status-report-responsive"
            color="link"
            onClick={_resetFilter}
          >
            <SvgIcons type={"refresh"} />
          </Button>

          <Button
            className="filterBarIcon"
            color="link"
            onClick={filterShowMobile}
          >
            <SvgIcons type={"filterIcon"} />
          </Button>
        </div>
      </div>
      <div
        onClick={filterShowMobile}
        className={`mobileFilterView ${isClassAdded ? "show" : ""}`}
      ></div>
      <div
        className={`filterContainer  responsiveFilter ${
          isClassAdded ? "show" : ""
        }`}
      >
        <div className="filterIcon">
          <SvgIcons type={"filterIcon"} />
          Filter by
        </div>
        <div className="mobileTitle">
          <h2>Filter by</h2>
          <Button
            color="link"
            className="closeButton"
            onClick={filterShowMobile}
          >
            <img
              src={require("../../../assets/img/close_grey.svg").default}
              alt="close"
              height={12}
            />
          </Button>
        </div>
        <Suspense fallback={<></>}>
          <AgentCompantStatusReportFilterComponent
            filters={payload}
            _filterOnChange={_filterOnChange}
            _downloadData={_downloadData}
          />
        </Suspense>
        <div className="clearButton">
          <Button size="md" color="primary" onClick={filterShowMobile}>
            Close
          </Button>
        </div>
      </div>

      <div className="downloadButtonPos">
        <Button color="link" outline onClick={() => _downloadData("download")}>
          Download
          <SvgIcons type={"download"} />
          {/* <img
            src={require(`../../../assets/img/download_blue.png`)}
            alt="download"
          /> */}
        </Button>
      </div>
      <Row>
        <Col md="12">
          <div className="verticalTabs">
            <Nav tabs>
              {agentClosingStatus?.map((each) => (
                <NavItem NavItem key={`NavItem_${each.value}`}>
                  <NavLink
                    className={activeTab === each.value ? "active" : ""}
                    onClick={() => {
                      _toggleTab(each.value);
                    }}
                  >
                    {/* <img
                      src={require(`../../../assets/img/${activeTab === each.value
                        ? each.imageBlueName
                        : each.imageBlackName
                        }.png`)}
                      alt={each.label}
                    /> */}
                    <SvgIcons type={`${each.icon}`} />
                    {each.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent>
              <TabPane>
                <Row>
                  <Col xs={6}>
                    {loading ? (
                      <SkeletonLoading
                        type="card"
                        count={1}
                        height={isForMobile ? 140 : 100}
                        width={isForMobile ? 150 : 600}
                      />
                    ) : (
                      <Card className="infoCard">
                        <CardBody>
                          <div className="infoContent">
                            <span>{activeTab}</span>

                            {userData?.type === DEFAULT_KEY_FOR_COMPANY ? (
                              <h4 className={`${getDropdownColor(activeTab)}`}>
                                {closingsBackup?.length
                                  ? // closings.length
                                    closingsBackup.reduce(
                                      (accumulator, currentObject) => {
                                        if (
                                          currentObject.status === activeTab
                                        ) {
                                          return accumulator + 1;
                                        } else {
                                          return accumulator;
                                        }
                                      },
                                      0
                                    )
                                  : "0"}
                              </h4>
                            ) : (
                              <h4 className={`${getDropdownColor(activeTab)}`}>
                                {closings?.[activeTab]?.length
                                  ? closings[activeTab].length
                                  : "0"}
                              </h4>
                            )}
                          </div>

                          <div className="icon">
                            {/* <img
                              src={
                                require("../../../assets/img/invoicepending.svg")
                                  .default
                              }
                              alt="notaryActs"
                            /> */}
                            <SvgIcons type={_getStatusIcon(activeTab)} />
                          </div>
                        </CardBody>
                      </Card>
                    )}
                  </Col>
                  <Col xs={6}>
                    {loading ? (
                      <SkeletonLoading
                        type="card"
                        count={1}
                        height={isForMobile ? 140 : 100}
                        width={isForMobile ? 150 : 600}
                      />
                    ) : (
                      <Card className="infoCard">
                        <CardBody>
                          <div className="infoContent">
                            <span>Agent Fee</span>
                            {userData?.type === DEFAULT_KEY_FOR_COMPANY ? (
                              <h4>
                                {closings?.length
                                  ? formatCurrencyValue(
                                      _calculateAgentTotalFee(closingsBackup)
                                    )
                                  : formatCurrencyValue(0)}
                              </h4>
                            ) : (
                              <h4>
                                {closings?.[activeTab]?.length
                                  ? formatCurrencyValue(
                                      _calculateAgentTotalFee(
                                        closings[activeTab]
                                      )
                                    )
                                  : formatCurrencyValue(0)}
                              </h4>
                            )}
                          </div>
                          <div className="icon">
                            <SvgIcons type={"invoicePaid"} />
                          </div>
                        </CardBody>
                      </Card>
                    )}
                  </Col>

                  {userData?.type === DEFAULT_KEY_FOR_COMPANY ? (
                    <Col md={12} className="mt-3">
                      <>
                        <Suspense fallback={<></>}>
                          {isForMobile ? (
                            <div className="hideDesktop">
                              <CustomCard
                                isPageStartFromOne={true}
                                pageNumber={pageNumber}
                                tableData={closings}
                                isPagination={false}
                                columnSizeMD={12}
                                totalCount={
                                  closingsBackup?.filter(
                                    (item) => item?.status === activeTab
                                  )?.length
                                }
                                rowSelection={false}
                                showTableLoading={loading}
                                cardDataFormat={_cardDataFormat}
                              />
                            </div>
                          ) : (
                            <div className="hideMobile">
                              {_tableFormatWithArray(closings)}
                            </div>
                          )}
                          <CustomPagination
                            data={closings}
                            dataCount={
                              closingsBackup?.filter(
                                (item) => item?.status === activeTab
                              )?.length
                            }
                            isPageStartFromOne={true}
                            pageNumber={pageNumber}
                            pageSize={10}
                            onPageChange={(pageNumber) =>
                              _paginateArray(pageNumber)
                            }
                            isHideForLessData={true}
                          />
                        </Suspense>
                      </>
                    </Col>
                  ) : (
                    <Col md={12} className="mt-3">
                      <>
                        <Suspense fallback={<></>}>
                          {!isForMobile ? (
                            <div className="hideMobile">
                              {_tableFormatWithArray(
                                closings[activeTab]?.slice(
                                  (pageNumber - 1) * 5,
                                  pageNumber * 5
                                )
                              )}
                            </div>
                          ) : (
                            <div className="hideDesktop">
                              <CustomCard
                                isPageStartFromOne={true}
                                pageNumber={pageNumber}
                                tableData={closings[activeTab]?.slice(
                                  pageNumber * 5 - 5,
                                  pageNumber * 5
                                )}
                                isPagination={false}
                                totalCount={closings[activeTab]?.length}
                                rowSelection={false}
                                showTableLoading={loading}
                                cardDataFormat={_cardDataFormat}
                              />
                            </div>
                          )}
                          <CustomPagination
                            data={closings[activeTab]?.slice(
                              pageNumber,
                              pageNumber + 5
                            )}
                            dataCount={closings[activeTab]?.length}
                            pageNumber={pageNumber}
                            pageSize={5}
                            onPageChange={(pageNumber) => {
                              setPageNumber(pageNumber);
                            }}
                            isPageStartFromOne={true}
                            isHideForLessData={true}
                          />
                        </Suspense>
                      </>
                    </Col>
                  )}
                </Row>
              </TabPane>
            </TabContent>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AgentCompanyStatusReport;
